// BoundaryUtils.js
// Shared utility — imported by AddPlant (Plant.js) and EditPlant.js
// Kept separate so neither form component depends on the other.

// ---------------------------------------------------------------------------
// convertBoundaryToJson
// ---------------------------------------------------------------------------
// Input : "11.234567, 73.345678, 10.234577, 73.345678, 11.45689, 73.23567999"
// Output: '[{"lat":11.234567,"lon":73.345678}, ...]'   (string, ready for DB)
// Rules : min 3 pairs, max 15 pairs, values must be valid lat/lon floats.
// ---------------------------------------------------------------------------
export const convertBoundaryToJson = (raw) => {
    if (!raw || !raw.trim()) return { json: null, error: "Boundary is required." };

    const tokens = raw.split(",").map((t) => t.trim()).filter(Boolean);

    if (tokens.length < 6)
        return { json: null, error: "Boundary must have at least 3 coordinate pairs (6 numbers)." };

    if (tokens.length % 2 !== 0)
        return { json: null, error: "Boundary has an odd number of values — each lat must have a matching lon." };

    if (tokens.length / 2 > 15)
        return { json: null, error: `Boundary must not exceed 15 coordinate pairs. You entered ${tokens.length / 2}.` };

    const points = [];
    for (let i = 0; i < tokens.length; i += 2) {
        const lat = parseFloat(tokens[i]);
        const lon = parseFloat(tokens[i + 1]);

        if (isNaN(lat) || isNaN(lon))
            return { json: null, error: `Invalid number at position ${i + 1}: "${tokens[i]}" / "${tokens[i + 1]}"` };

        if (lat < -90 || lat > 90)
            return { json: null, error: `Latitude ${lat} at position ${i + 1} is out of range (-90 to 90).` };

        if (lon < -180 || lon > 180)
            return { json: null, error: `Longitude ${lon} at position ${i + 2} is out of range (-180 to 180).` };

        points.push({ lat, lon });
    }

    return { json: JSON.stringify(points), error: null };
};

// ---------------------------------------------------------------------------
// convertJsonToBoundaryCSV
// ---------------------------------------------------------------------------
// Reverses the above — reads DB value back into editable CSV for the textarea.
// Input : '[{"lat":11.234,"lon":73.345}, ...]'
// Output: "11.234, 73.345, ..."
// ---------------------------------------------------------------------------
export const convertJsonToBoundaryCSV = (jsonStr) => {
    if (!jsonStr) return "";
    try {
        const points = JSON.parse(jsonStr);
        if (!Array.isArray(points)) return jsonStr;
        return points.map((p) => `${p.lat}, ${p.lon}`).join(", ");
    } catch {
        return jsonStr; // not valid JSON — show raw so user can fix it
    }
};
